// Module: key-hierarchy
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §3
//
// Every key derivation in the system. Nothing else calls PBKDF2, HKDF or AES-KW.
// Pure over bytes: no ceremony, no network, no DOM.

import { normalize as normalizePassphrase } from "../passphrase_2026-08-27/passphrase.js"

export type KdfParams = {
  alg: "PBKDF2-HMAC-SHA256"
  iterations: number
  salt: Uint8Array
}

export type SubkeyPurpose = "content" | "record-mac"
export type RandomSource = (out: Uint8Array) => void

export const MIN_ITERATIONS = 600_000
export const DEFAULT_ITERATIONS = 1_000_000
export const DEK_BYTES = 32
export const WRAPPED_DEK_BYTES = 40
export const KDF_SALT_BYTES = 16
export const HKDF_SALT_BYTES = 16
export const SECOND_FACTOR_BYTES = 32
export const PRF_SEED_BYTES = 32
export const LOCAL_SALT_BYTES = 16
export const SEALED_OVERHEAD_BYTES = LOCAL_SALT_BYTES + 12 + 16 // salt + nonce + GCM tag

const ALLOWED_ALGS = new Set(["PBKDF2-HMAC-SHA256"])

export type KeyHierarchyErrorCode =
  | "WeakPassphrase"
  | "WeakKdfParams"
  | "UnwrapFailed"
  | "InvalidLength"

export class KeyHierarchyError extends Error {
  readonly code: KeyHierarchyErrorCode
  constructor(code: KeyHierarchyErrorCode, message?: string, options?: { cause?: unknown }) {
    super(message ?? code, options)
    this.name = "KeyHierarchyError"
    this.code = code
  }
}

const enc = new TextEncoder()
const src = (u: Uint8Array): BufferSource => u as unknown as BufferSource

const defaultRng: RandomSource = (out) => {
  crypto.getRandomValues(out)
}

function requireLength(value: Uint8Array, expected: number, name: string): void {
  if (value.length !== expected) {
    throw new KeyHierarchyError(
      "InvalidLength",
      `${name} must be ${expected} bytes, got ${value.length}`,
    )
  }
}

/**
 * The client's own floor, applied regardless of what the bootstrap record claims (spec §2.5).
 * The record is publicly writable; trusting its parameters would let a writer force weak
 * derivation.
 */
function requireStrongKdf(kdf: KdfParams): void {
  if (!ALLOWED_ALGS.has(kdf.alg)) {
    throw new KeyHierarchyError("WeakKdfParams", `unsupported kdf algorithm: ${String(kdf.alg)}`)
  }
  if (!Number.isInteger(kdf.iterations) || kdf.iterations < MIN_ITERATIONS) {
    throw new KeyHierarchyError(
      "WeakKdfParams",
      `iterations must be at least ${MIN_ITERATIONS}, got ${kdf.iterations}`,
    )
  }
  requireLength(kdf.salt, KDF_SALT_BYTES, "kdf.salt")
}

async function hardenPassphrase(passphrase: string, kdf: KdfParams): Promise<Uint8Array> {
  requireStrongKdf(kdf)
  const material = await crypto.subtle.importKey(
    "raw",
    src(enc.encode(normalizePassphrase(passphrase))),
    "PBKDF2",
    false,
    ["deriveBits"],
  )
  return new Uint8Array(
    await crypto.subtle.deriveBits(
      { name: "PBKDF2", salt: src(kdf.salt), iterations: kdf.iterations, hash: "SHA-256" },
      material,
      256,
    ),
  )
}

async function hkdfBits(opts: {
  ikm: Uint8Array
  salt: Uint8Array
  info: Uint8Array
}): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("raw", src(opts.ikm), "HKDF", false, ["deriveBits"])
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: src(opts.salt), info: src(opts.info) },
    key,
    256,
  )
  return new Uint8Array(bits)
}

export function generateKdfParams(
  opts: { iterations?: number; rng?: RandomSource } = {},
): KdfParams {
  const salt = new Uint8Array(KDF_SALT_BYTES)
  ;(opts.rng ?? defaultRng)(salt)
  const iterations = opts.iterations ?? DEFAULT_ITERATIONS
  const params: KdfParams = { alg: "PBKDF2-HMAC-SHA256", iterations, salt }
  requireStrongKdf(params)
  return params
}

export function generateDek(rng: RandomSource = defaultRng): Uint8Array {
  const dek = new Uint8Array(DEK_BYTES)
  rng(dek)
  return dek
}

export function generateHkdfSalt(rng: RandomSource = defaultRng): Uint8Array {
  const salt = new Uint8Array(HKDF_SALT_BYTES)
  rng(salt)
  return salt
}

/**
 * KEK = HKDF(IKM = PBKDF2(passphrase) ‖ secondFactor, salt = hkdfSalt, info = "vault-kek-v1"‖slotId)
 *
 * Argument order is load-bearing and fixed: v1.0's ambiguity here is exactly what forces an
 * implementer to guess, and a guess produces a vault nobody can open.
 */
export async function deriveKek(opts: {
  passphrase: string
  secondFactor: Uint8Array
  hkdfSalt: Uint8Array
  slotId: string
  kdf: KdfParams
}): Promise<CryptoKey> {
  // No passphrase check here. Spec §0: the strength of the user's password is the user's call,
  // and this module's job is to derive a key from whatever they chose, not to referee it. The UI
  // names the risk plainly and then does as it is told (`app`, the create screen).
  //
  // Refusing here was also worse than useless: `deriveKek` is on the *unlock* path, so any future
  // tightening of the floor would have locked people out of vaults that already existed, at the
  // one moment they could do nothing about it.
  requireStrongKdf(opts.kdf)
  requireLength(opts.secondFactor, SECOND_FACTOR_BYTES, "secondFactor")
  requireLength(opts.hkdfSalt, HKDF_SALT_BYTES, "hkdfSalt")

  const keyPass = await hardenPassphrase(opts.passphrase, opts.kdf)

  const ikm = new Uint8Array(keyPass.length + opts.secondFactor.length)
  ikm.set(keyPass, 0)
  ikm.set(opts.secondFactor, keyPass.length)

  const kekBytes = await hkdfBits({
    ikm,
    salt: opts.hkdfSalt,
    info: enc.encode(`vault-kek-v1${opts.slotId}`),
  })

  return crypto.subtle.importKey("raw", src(kekBytes), { name: "AES-KW" }, false, [
    "wrapKey",
    "unwrapKey",
  ])
}

export async function wrapDek(opts: { kek: CryptoKey; dek: Uint8Array }): Promise<Uint8Array> {
  requireLength(opts.dek, DEK_BYTES, "dek")
  // AES-KW can only wrap an extractable key, so the DEK is imported extractable here and
  // discarded. This is the narrowest window that still supports enrolling a backup authenticator.
  const wrappable = await crypto.subtle.importKey("raw", src(opts.dek), { name: "AES-GCM" }, true, [
    "encrypt",
  ])
  return new Uint8Array(await crypto.subtle.wrapKey("raw", wrappable, opts.kek, "AES-KW"))
}

/**
 * Returns a NON-EXTRACTABLE HKDF key. After unwrap the DEK bytes never cross this interface
 * again; the key can derive subkeys but cannot be read by script.
 */
export async function unwrapDek(opts: {
  kek: CryptoKey
  wrappedDek: Uint8Array
}): Promise<CryptoKey> {
  try {
    return await crypto.subtle.unwrapKey(
      "raw",
      src(opts.wrappedDek),
      opts.kek,
      "AES-KW",
      { name: "HKDF" },
      false,
      ["deriveBits"],
    )
  } catch (cause) {
    // One code for a wrong passphrase, a wrong authenticator and a tampered wrappedDek.
    throw new KeyHierarchyError("UnwrapFailed", "could not unwrap the vault key", { cause })
  }
}

/**
 * Moves a wrapped DEK from one slot's KEK to another's without the bytes ever crossing this
 * interface. This is what makes slot management possible at all: after `unwrapDek` the DEK is a
 * non-extractable key, so a caller holding one cannot wrap it for a new authenticator. The
 * unwrap-and-rewrap happens here, where the extractable form never escapes.
 */
export async function rewrapDek(opts: {
  fromKek: CryptoKey
  toKek: CryptoKey
  wrappedDek: Uint8Array
}): Promise<Uint8Array> {
  let extractable: CryptoKey
  try {
    extractable = await crypto.subtle.unwrapKey(
      "raw",
      src(opts.wrappedDek),
      opts.fromKek,
      "AES-KW",
      { name: "AES-GCM" },
      true,
      ["encrypt"],
    )
  } catch (cause) {
    throw new KeyHierarchyError("UnwrapFailed", "could not unwrap the vault key", { cause })
  }
  return new Uint8Array(await crypto.subtle.wrapKey("raw", extractable, opts.toKek, "AES-KW"))
}

/**
 * Seals the hardware secret so this machine can reproduce it from the password alone.
 *
 * This is what removes the hardware key from everyday use. `KeyHard` is normally obtainable only
 * by touching the authenticator; sealed under a key derived from the password it can live on the
 * laptop instead, and opening the vault needs nothing but the password.
 *
 * It is never uploaded. The cloud therefore holds ciphertext whose key requires a factor the cloud
 * has never seen, so a total breach of the storage provider yields nothing to attack offline.
 *
 * The residual is a machine-local one: someone holding the laptop can attack this blob offline at
 * PBKDF2 cost. That is the bar the password has to clear (spec §1.4).
 */
export async function sealHardSecret(opts: {
  passphrase: string
  kdf: KdfParams
  hardSecret: Uint8Array
  rng?: RandomSource
}): Promise<Uint8Array> {
  requireLength(opts.hardSecret, SECOND_FACTOR_BYTES, "hardSecret")
  return sealLocal({ passphrase: opts.passphrase, kdf: opts.kdf, plaintext: opts.hardSecret, ...(opts.rng ? { rng: opts.rng } : {}) })
}

/**
 * The same envelope as `sealHardSecret`, for anything else this machine wants to keep under the
 * password rather than in the clear — the vault-name cache being the reason it exists (§3.6).
 *
 * Names are not secrets against the network; nothing here is uploaded. They are secret against
 * the adversary this system actually has: someone holding the laptop (§1.4), who would otherwise
 * read a list of vault names off it without knowing any password.
 */
export async function sealLocal(opts: {
  passphrase: string
  kdf: KdfParams
  plaintext: Uint8Array
  rng?: RandomSource
}): Promise<Uint8Array> {
  const rng = opts.rng ?? defaultRng

  const localSalt = new Uint8Array(LOCAL_SALT_BYTES)
  rng(localSalt)
  const nonce = new Uint8Array(12)
  rng(nonce)

  const key = await localKey(opts.passphrase, opts.kdf, localSalt)
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: src(nonce) }, key, src(opts.plaintext)),
  )

  const sealed = new Uint8Array(localSalt.length + nonce.length + ciphertext.length)
  sealed.set(localSalt, 0)
  sealed.set(nonce, localSalt.length)
  sealed.set(ciphertext, localSalt.length + nonce.length)
  return sealed
}

/** Opens anything `sealLocal` sealed. `openHardSecret` is this under its older name. */
export const openLocal = (opts: {
  passphrase: string
  kdf: KdfParams
  sealed: Uint8Array
}): Promise<Uint8Array> => openHardSecret(opts)

/** Throws `UnwrapFailed` for a wrong password or a tampered blob — the same code, no detail. */
export async function openHardSecret(opts: {
  passphrase: string
  kdf: KdfParams
  sealed: Uint8Array
}): Promise<Uint8Array> {
  if (opts.sealed.length <= SEALED_OVERHEAD_BYTES) {
    throw new KeyHierarchyError("InvalidLength", "sealed secret is too short")
  }
  const localSalt = opts.sealed.subarray(0, LOCAL_SALT_BYTES)
  const nonce = opts.sealed.subarray(LOCAL_SALT_BYTES, LOCAL_SALT_BYTES + 12)
  const ciphertext = opts.sealed.subarray(LOCAL_SALT_BYTES + 12)

  const key = await localKey(opts.passphrase, opts.kdf, localSalt)
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt({ name: "AES-GCM", iv: src(nonce) }, key, src(ciphertext)),
    )
  } catch (cause) {
    throw new KeyHierarchyError("UnwrapFailed", "could not open the sealed secret", { cause })
  }
}

async function localKey(
  passphrase: string,
  kdf: KdfParams,
  localSalt: Uint8Array,
): Promise<CryptoKey> {
  const keyPass = await hardenPassphrase(passphrase, kdf)
  const bits = await hkdfBits({
    ikm: keyPass,
    salt: localSalt,
    info: enc.encode("vault-local-v1"),
  })
  return crypto.subtle.importKey("raw", src(bits), { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ])
}

/**
 * Ed25519 seed authenticating an adopted machine to the Worker.
 *
 * Reading the vault needs authorization, and everyday opening performs no WebAuthn ceremony — so
 * the assertion that used to provide it is gone. This replaces it, and requires BOTH factors:
 * the password, and `KeyHard`, which only an adopted machine or the hardware key can supply.
 */
export async function deviceAuthSeed(opts: {
  passphrase: string
  kdf: KdfParams
  hardSecret: Uint8Array
}): Promise<Uint8Array> {
  requireLength(opts.hardSecret, SECOND_FACTOR_BYTES, "hardSecret")
  const keyPass = await hardenPassphrase(opts.passphrase, opts.kdf)
  const ikm = new Uint8Array(keyPass.length + opts.hardSecret.length)
  ikm.set(keyPass, 0)
  ikm.set(opts.hardSecret, keyPass.length)
  return hkdfBits({
    ikm,
    salt: opts.kdf.salt,
    info: enc.encode("vault-device-auth-v1"),
  })
}

const SUBKEY_INFO: Record<SubkeyPurpose, string> = {
  content: "vault-content-encryption-v1",
  "record-mac": "vault-record-mac-v1",
}

/** The DEK is never used directly as a cipher key (spec §3.5). */
export async function deriveSubkey(dek: CryptoKey, purpose: SubkeyPurpose): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: src(new Uint8Array(0)),
      info: src(enc.encode(SUBKEY_INFO[purpose])),
    },
    dek,
    256,
  )
  return purpose === "content"
    ? crypto.subtle.importKey("raw", bits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"])
    : crypto.subtle.importKey("raw", bits, { name: "HMAC", hash: "SHA-256" }, false, [
        "sign",
        "verify",
      ])
}

/** The second factor for a recovery slot: a slot whose hardware secret is a written-down code. */
export async function recoverySecret(
  codeBytes: Uint8Array,
  hkdfSalt: Uint8Array,
): Promise<Uint8Array> {
  requireLength(hkdfSalt, HKDF_SALT_BYTES, "hkdfSalt")
  return hkdfBits({ ikm: codeBytes, salt: hkdfSalt, info: enc.encode("vault-recovery-v1") })
}

/** Ed25519 seed authenticating the recovery path to the Worker (spec §3.4, §5.4). */
export async function recoverySigningSeed(
  codeBytes: Uint8Array,
  hkdfSalt: Uint8Array,
): Promise<Uint8Array> {
  requireLength(hkdfSalt, HKDF_SALT_BYTES, "hkdfSalt")
  return hkdfBits({ ikm: codeBytes, salt: hkdfSalt, info: enc.encode("vault-recovery-auth-v1") })
}

/**
 * Splits one PRF output into the two secrets a named vault needs (spec §2.2, §3.3).
 *
 * The authenticator is asked to evaluate a *single* salt, so this keeps working on a platform that
 * forwards one PRF salt but not two — a stronger assumption than the design ever needed to make.
 * The separation that used to come from a second PRF input now comes from an HKDF info label,
 * which is how every other purpose in this module is already separated (§3.5).
 *
 * `locator` addresses the vault; the VaultID derived from it is published. `hard` is key material.
 * Publishing something derived from `locator` reveals nothing about `hard`, because recovering the
 * seed from either output would mean inverting HKDF-Expand.
 */
export async function splitPrfSeed(
  seed: Uint8Array,
): Promise<{ locator: Uint8Array; hard: Uint8Array }> {
  requireLength(seed, PRF_SEED_BYTES, "seed")
  const empty = new Uint8Array(0)
  const [locator, hard] = await Promise.all([
    hkdfBits({ ikm: seed, salt: empty, info: enc.encode("vault-locator-v2") }),
    hkdfBits({ ikm: seed, salt: empty, info: enc.encode("vault-key-v2") }),
  ])
  return { locator, hard }
}
